import { canonicalTag } from './canonicalTag';

// Checkboxes the panel lists, and the query limit. The same number on purpose:
// the sort never changes, so anything fetched past what is shown is discarded.
export const MAX_VISIBLE_TAGS = 50;

type Params = {
  matchedTags: string[];
  selectedTags: string[];
};

/**
 * Selected tags are listed first and always, even when the current search
 * excludes them: a ticked tag that vanished could never be unticked.
 *
 * Deduplicated on the canonical form, because a selected tag carries whatever
 * spelling the row the recruiter clicked happened to use while the catalog
 * holds the organisation's one label for it. Comparing the strings instead
 * lists `VISA:Egypt` and `visa:Egypt` as two options, both ticked by one
 * selection, and either one clearing both.
 *
 * The catalog's spelling wins the label for a tag in both lists, for the same
 * reason it is the one the filter panel shows everywhere else.
 */
export const buildTagFilterOptions = ({
  matchedTags,
  selectedTags
}: Params): string[] => {
  const visibleTags = matchedTags.slice(0, MAX_VISIBLE_TAGS);

  const labels = new Map<string, string>();
  for (const tag of visibleTags) labels.set(canonicalTag(tag), tag);
  for (const tag of selectedTags) {
    if (!labels.has(canonicalTag(tag))) labels.set(canonicalTag(tag), tag);
  }

  const ordered = [
    ...new Set([...selectedTags, ...visibleTags].map(canonicalTag))
  ];

  return ordered.flatMap((key) => {
    const label = labels.get(key);
    return label ? [label] : [];
  });
};
